import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { Card, Col, Row, Statistic } from "antd";

const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;

  const raw = String(value).trim();
  const normalizedRaw = raw.toLowerCase();
  if (normalizedRaw === "inf" || normalizedRaw === "inf.") {
    return Number.POSITIVE_INFINITY;
  }
  if (normalizedRaw === "infinity") {
    return Number.POSITIVE_INFINITY;
  }
  if (!raw) return null;

  const hasComma = raw.includes(",");
  const hasDot = raw.includes(".");

  let normalized = raw;
  if (hasComma && hasDot) {
    const lastComma = raw.lastIndexOf(",");
    const lastDot = raw.lastIndexOf(".");
    normalized =
      lastComma > lastDot
        ? raw.replace(/\./g, "").replace(",", ".")
        : raw.replace(/,/g, "");
  } else if (hasComma) {
    normalized = raw.replace(",", ".");
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const formatOneDecimalPtBr = (value) => {
  const parsed = parseNumericValue(value);
  if (parsed === null) return "0,0";
  if (!Number.isFinite(parsed)) return "Inf.";
  return Number(parsed).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

const formatPercentDisplay = (value) => {
  const formatted = formatOneDecimalPtBr(value);
  return formatted === "Inf." ? "Inf." : `${formatted}%`;
};

const formatTwoDecimalsPtBr = (value) =>
  Number(parseNumericValue(value) ?? 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const Variacao = ({ titulo, eixo, formatter, suffix }) => (
  <Card bordered={true} style={{ height: "100%" }}>
    <Statistic
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
      title={titulo}
      value={eixo?.percent}
      formatter={formatter}
      suffix={suffix}
      prefix={
        eixo?.color === "red" ? (
          <ArrowDownOutlined />
        ) : eixo?.color === "green" ? (
          <ArrowUpOutlined />
        ) : null
      }
      valueStyle={{
        color:
          eixo?.color === "red"
            ? "#cf1322"
            : eixo?.color === "green"
              ? "#3f8600"
              : "inherit",
      }}
    />
  </Card>
);

export default function CollectionDetailed({ kpiData }) {
  const cardStyle = {
    height: "100%",
  };

  const statisticStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Row gutter={[24, 24]}>
        <Col span={8}>
          <Card bordered={true} style={cardStyle}>
            <Statistic
              style={statisticStyle}
              title="Faturamento antes da mudança"
              value={kpiData?.revenue.before_change}
              formatter={formatTwoDecimalsPtBr}
              prefix="R$"
              suffix="/dia"
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={true} style={cardStyle}>
            <Statistic
              style={statisticStyle}
              title="Faturamento após a mudança"
              value={kpiData?.revenue.after_change}
              formatter={formatTwoDecimalsPtBr}
              prefix="R$"
              suffix="/dia"
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={true} style={cardStyle}>
            <Statistic
              style={statisticStyle}
              title="Variação do faturamento"
              value={kpiData?.revenue_variation.percent}
              formatter={formatPercentDisplay}
              prefix={
                kpiData?.revenue_variation.color === "red" ? (
                  <ArrowDownOutlined />
                ) : kpiData?.revenue_variation.color === "green" ? (
                  <ArrowUpOutlined />
                ) : null
              }
              valueStyle={{
                color:
                  kpiData?.revenue_variation.color === "red"
                    ? "#cf1322"
                    : kpiData?.revenue_variation.color === "green"
                      ? "#3f8600"
                      : "inherit",
              }}
            />
          </Card>
        </Col>
      </Row>
      <Row gutter={[24, 24]}>
        <Col span={8}>
          <Card bordered={true} style={cardStyle}>
            <Statistic
              style={statisticStyle}
              title="Tempo de atenção antes da mudança"
              value={kpiData?.permanence.before_change}
              formatter={formatTwoDecimalsPtBr}
              suffix="min/dia"
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={true} style={cardStyle}>
            <Statistic
              style={statisticStyle}
              title="Tempo de atenção após a mudança"
              value={kpiData?.permanence.after_change}
              formatter={formatTwoDecimalsPtBr}
              suffix="min/dia"
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={true} style={cardStyle}>
            <Statistic
              style={statisticStyle}
              title="Variação do tempo de atenção"
              value={kpiData?.permanence_variation.percent}
              formatter={formatOneDecimalPtBr}
              suffix="%"
              prefix={
                kpiData?.permanence_variation.color === "red" ? (
                  <ArrowDownOutlined />
                ) : kpiData?.permanence_variation.color === "green" ? (
                  <ArrowUpOutlined />
                ) : null
              }
              valueStyle={{
                color:
                  kpiData?.permanence_variation.color === "red"
                    ? "#cf1322"
                    : kpiData?.permanence_variation.color === "green"
                      ? "#3f8600"
                      : "inherit",
              }}
            />
          </Card>
        </Col>
      </Row>
      {kpiData?.occupation && (
        <Row gutter={[24, 24]}>
          <Col span={8}>
            <Card bordered={true} style={cardStyle}>
              <Statistic
                style={statisticStyle}
                title="Ocupação antes da mudança"
                value={kpiData.occupation.before_change}
                formatter={formatOneDecimalPtBr}
                suffix="%"
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card bordered={true} style={cardStyle}>
              <Statistic
                style={statisticStyle}
                title="Ocupação após a mudança"
                value={kpiData.occupation.after_change}
                formatter={formatOneDecimalPtBr}
                suffix="%"
              />
            </Card>
          </Col>
          <Col span={8}>
            <Variacao
              titulo="Variação da ocupação"
              eixo={kpiData.occupation_variation}
              formatter={formatOneDecimalPtBr}
              suffix="%"
            />
          </Col>
        </Row>
      )}
    </div>
  );
}
